import {useAnalytics} from '@github-ui/use-analytics'

import {useEffect, useState} from 'react'
import {clsx} from 'clsx'

import {
  type DirectMergeActionsSectionProps,
  DirectMergeActionsSection,
  type MergeButtonFocusProps,
} from './DirectMergeActionsSection'
import type {PullRequestMergeAction} from '../../../types'
import {BypassMergeRequirementsToggle} from './BypassMergeRequirementsToggle'
import type {
  PullRequestMergeRequirementsPayload,
  JSONAPIPullRequestPayload,
} from '../../../page-data/payloads/merge-box'
import type {Status} from '../../../helpers/mergeability-status'
import type {MergeBoxRollupStatus} from '../../../helpers/merge-box-status-calculator/types'
import {useIsMergeBoxSidePanel} from '../../../contexts/MergeBoxLayoutContext'
import {useIsFeatureStateEnabled} from '@github-ui/pull-requests/use-feature-state'
import type {PullRequestUserActionStack} from '@github-ui/pull-requests/analytics/pull-request-user-action'
import type {StackSectionStackEntry} from '../../../helpers/merge-box-status-calculator/stack-section-types'
import {DisableAutoMerge} from './DisableAutoMerge'
import {AddToMergeQueueSection} from './AddToMergeQueueSection'
import {deriveMergeControlModel} from './derive-merge-control-model'

export type MergeSectionProps = {
  baseRefName: string
  canUserPushToBase: boolean
  conflictsCondition: DirectMergeActionsSectionProps['conflictsCondition']
  handleConfirmingMergeInfo: (isConfirming: boolean) => void
  mergeCurrentlyBlockedForStacks?: boolean
  helpUrl: string
  isConfirmingMergeInfo: boolean
  isStackMergeInProgress?: boolean
  isStacked?: boolean
  stackedEntriesToMergeCount?: number
  stackedEntries?: StackSectionStackEntry[]
  stackMetadata?: PullRequestUserActionStack
  mergeBoxRollupStatus: MergeBoxRollupStatus
  mergeRequirements: PullRequestMergeRequirementsPayload
  onStackMergePendingChange?: (pendingAction?: PullRequestMergeAction) => void
  status: Status
  pullRequest: Omit<JSONAPIPullRequestPayload, 'baseRefName'> // use baseRefName prop from MergeSectionStatus
} & MergeButtonFocusProps

function assertNeverControl(control: never): never {
  throw new Error(`Unexpected merge control: ${JSON.stringify(control)}`)
}

/**
 * Renders the merge actions section of the merge box, specifically the merge button
 * Depending on configurations, a viewer can select a merge method, merge directly, or add the pull request to the merge queue
 */
export function MergeSection({
  baseRefName,
  canUserPushToBase,
  conflictsCondition,
  handleConfirmingMergeInfo,
  helpUrl,
  mergeCurrentlyBlockedForStacks,
  isConfirmingMergeInfo,
  isStackMergeInProgress = false,
  mergeBoxRollupStatus,
  mergeRequirements,
  onStackMergePendingChange,
  setShouldFocusPrimaryMergeButton,
  shouldFocusPrimaryMergeButton,
  stackedEntriesToMergeCount,
  status,
  isStacked,
  stackedEntries,
  stackMetadata,
  pullRequest,
}: MergeSectionProps & MergeButtonFocusProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const isSidePanel = useIsMergeBoxSidePanel()
  const [isAdminBypassToggleChecked, setIsAdminBypassToggleChecked] = useState(false)
  const isPullRequestStacksAsyncMergeEnabled = useIsFeatureStateEnabled('pullRequestStacksAsyncMerge')

  const {
    advisoryWorkspace,
    autoMergeRequest,
    headRepository,
    id,
    isCrossRepo,
    isDraft,
    isInMergeQueue,
    mergeQueue,
    mergeStateStatus,
    numberOfCommits,
    viewerMergeActions,
    viewerCanAddAndRemoveFromMergeQueue,
    viewerCanAddToMergeQueueSolo,
    viewerCanDisableAutoMerge,
    viewerCanEnableAutoMerge,
    viewerCanAdminBypassMergeRequirements,
  } = pullRequest

  const {
    commitMessageBody,
    commitMessageHeadline,
    defaultCommitAuthorEmail,
    possibleCommitAuthorEmails,
    state: mergeRequirementsState,
  } = mergeRequirements

  const mergeControlModel = deriveMergeControlModel({
    advisoryWorkspace,
    autoMergeRequest,
    isAdminBypassToggleChecked,
    isConfirmingMergeInfo,
    isPullRequestStacksAsyncMergeEnabled,
    isStackMergeInProgress,
    isStacked,
    mergeRequirementsState,
    mergeStateStatus,
    stackedEntries,
    stackedEntriesToMergeCount,
    status,
    viewerCanAdminBypassMergeRequirements,
    viewerCanEnableAutoMerge,
    viewerMergeActions,
  })

  useEffect(() => {
    if (mergeControlModel.isMergeable) {
      sendAnalyticsEvent('merge_section.view_ready_to_merge', 'MERGEBOX_MERGE_SECTION', {pullRequestId: id})
    }
  }, [mergeControlModel.isMergeable, sendAnalyticsEvent, id])

  if (mergeControlModel.visibility === 'absent') return null

  const {
    control,
    isMergeQueueEnabled,
    isMergeable,
    isBypassAllowed,
    stackPositionInStack,
    stackSize,
    visibility,
    shouldRenderBypassToggle,
  } = mergeControlModel

  let mergeComponent
  switch (control.type) {
    case 'disable-auto-merge':
      mergeComponent = (
        <DisableAutoMerge
          autoMergeRequest={autoMergeRequest}
          isMergeQueueEnabled={isMergeQueueEnabled}
          viewerCanDisableAutoMerge={viewerCanDisableAutoMerge}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
        />
      )
      break
    case 'inactive-direct':
      mergeComponent = (
        <DirectMergeActionsSection
          baseRefName={baseRefName}
          canUserPushToBase={canUserPushToBase}
          commitMessageBody={commitMessageBody}
          commitMessageHeadline={commitMessageHeadline}
          conflictsCondition={conflictsCondition}
          defaultCommitAuthorEmail={defaultCommitAuthorEmail}
          mergeable={false}
          mergeCurrentlyBlockedForStacks={mergeCurrentlyBlockedForStacks}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          headRepository={headRepository}
          helpUrl={helpUrl}
          isAdminBypassToggleChecked={false}
          isAdminBypassToggleVisible={false}
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          isCrossRepo={isCrossRepo}
          isDraft={isDraft}
          isStacked={isStacked}
          mergeBoxRollupStatus={mergeBoxRollupStatus}
          mergeRequirementsState={mergeRequirementsState}
          numberOfCommits={numberOfCommits}
          onStackMergePendingChange={onStackMergePendingChange}
          possibleCommitAuthorEmails={possibleCommitAuthorEmails}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
          shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
          stackedEntriesToMergeCount={stackedEntriesToMergeCount}
          stackMetadata={stackMetadata}
          stackPositionInStack={stackPositionInStack}
          stackSize={stackSize}
          status={status}
          viewerMergeActions={viewerMergeActions}
        />
      )
      break
    case 'merge-queue-bypass':
      mergeComponent = (
        <DirectMergeActionsSection
          baseRefName={baseRefName}
          canUserPushToBase={canUserPushToBase}
          commitMessageBody={commitMessageBody}
          commitMessageHeadline={commitMessageHeadline}
          conflictsCondition={conflictsCondition}
          defaultCommitAuthorEmail={defaultCommitAuthorEmail}
          mergeable={isMergeable}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          headRepository={headRepository}
          helpUrl={helpUrl}
          isAdminBypassToggleVisible={isBypassAllowed}
          isAdminBypassToggleChecked
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          isCrossRepo={isCrossRepo}
          isDraft={isDraft}
          isStacked={isStacked}
          mergeBoxRollupStatus={mergeBoxRollupStatus}
          mergeRequirementsState={mergeRequirementsState}
          numberOfCommits={numberOfCommits}
          onStackMergePendingChange={onStackMergePendingChange}
          possibleCommitAuthorEmails={possibleCommitAuthorEmails}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
          shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
          stackedEntriesToMergeCount={stackedEntriesToMergeCount}
          stackMetadata={stackMetadata}
          stackPositionInStack={stackPositionInStack}
          stackSize={stackSize}
          status={status}
          viewerMergeActions={viewerMergeActions}
        />
      )
      break
    case 'merge-queue':
      mergeComponent = (
        <AddToMergeQueueSection
          autoMergeRequest={autoMergeRequest}
          baseRefName={baseRefName}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          mergeCurrentlyBlockedForStacks={mergeCurrentlyBlockedForStacks}
          isDraft={isDraft}
          isInMergeQueue={isInMergeQueue}
          mergeQueue={mergeQueue}
          mergeBoxRollupStatus={mergeBoxRollupStatus}
          mergeRequirementsState={mergeRequirementsState}
          mergeStateStatus={mergeStateStatus}
          onStackMergePendingChange={onStackMergePendingChange}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
          shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
          stackedEntriesToMergeCount={stackedEntriesToMergeCount}
          status={status}
          viewerCanAddAndRemoveFromMergeQueue={viewerCanAddAndRemoveFromMergeQueue}
          viewerCanAddToMergeQueueSolo={viewerCanAddToMergeQueueSolo}
          isStacked={isStacked}
          stackMetadata={stackMetadata}
          stackPositionInStack={stackPositionInStack}
          stackSize={stackSize}
        />
      )
      break
    case 'auto-merge':
      mergeComponent = (
        <DirectMergeActionsSection
          baseRefName={baseRefName}
          canUserPushToBase={canUserPushToBase}
          commitMessageBody={commitMessageBody}
          commitMessageHeadline={commitMessageHeadline}
          conflictsCondition={conflictsCondition}
          defaultCommitAuthorEmail={defaultCommitAuthorEmail}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          headRepository={headRepository}
          helpUrl={helpUrl}
          mergeable={isMergeable}
          isAdminBypassToggleChecked={false}
          isAdminBypassToggleVisible={isBypassAllowed}
          isAutoMergeAllowed={control.isAutoMergeAllowed}
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          isCrossRepo={isCrossRepo}
          isDraft={isDraft}
          isStacked={isStacked}
          mergeBoxRollupStatus={mergeBoxRollupStatus}
          mergeRequirementsState={mergeRequirementsState}
          numberOfCommits={numberOfCommits}
          onStackMergePendingChange={onStackMergePendingChange}
          possibleCommitAuthorEmails={possibleCommitAuthorEmails}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
          shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
          stackedEntriesToMergeCount={stackedEntriesToMergeCount}
          stackMetadata={stackMetadata}
          stackPositionInStack={stackPositionInStack}
          stackSize={stackSize}
          status={status}
          viewerMergeActions={viewerMergeActions}
        />
      )
      break
    case 'direct':
      mergeComponent = (
        <DirectMergeActionsSection
          baseRefName={baseRefName}
          canUserPushToBase={canUserPushToBase}
          commitMessageBody={commitMessageBody}
          commitMessageHeadline={commitMessageHeadline}
          conflictsCondition={conflictsCondition}
          defaultCommitAuthorEmail={defaultCommitAuthorEmail}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          headRepository={headRepository}
          helpUrl={helpUrl}
          isAdminBypassToggleChecked={isAdminBypassToggleChecked}
          isAdminBypassToggleVisible={isBypassAllowed}
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          isCrossRepo={isCrossRepo}
          isDraft={isDraft}
          mergeable={isMergeable}
          mergeCurrentlyBlockedForStacks={mergeCurrentlyBlockedForStacks}
          isStacked={isStacked}
          mergeBoxRollupStatus={mergeBoxRollupStatus}
          mergeRequirementsState={mergeRequirementsState}
          numberOfCommits={numberOfCommits}
          onStackMergePendingChange={onStackMergePendingChange}
          possibleCommitAuthorEmails={possibleCommitAuthorEmails}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
          shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
          stackedEntriesToMergeCount={stackedEntriesToMergeCount}
          stackMetadata={stackMetadata}
          stackPositionInStack={stackPositionInStack}
          stackSize={stackSize}
          status={status}
          viewerMergeActions={viewerMergeActions}
        />
      )
      break
    default:
      mergeComponent = assertNeverControl(control satisfies never)
  }

  return (
    <div
      hidden={visibility === 'hidden'}
      className={clsx(
        isSidePanel ? 'tmp-pt-3 tmp-px-3' : 'bgColor-muted borderColor-muted tmp-p-3',
        isConfirmingMergeInfo ? 'rounded-2' : 'rounded-bottom-2',
      )}
    >
      {shouldRenderBypassToggle && (
        <div className="tmp-mb-3">
          <BypassMergeRequirementsToggle
            checked={isAdminBypassToggleChecked}
            onToggleChecked={() => setIsAdminBypassToggleChecked(previous => !previous)}
          />
        </div>
      )}
      {mergeComponent}
    </div>
  )
}
